/** Stable id key for matching rows (5 and "5" are the same; temp string ids stay distinct). */
export function rowIdKey(
  id: number | string | null | undefined,
): string | null {
  if (id === null || id === undefined) return null;
  return String(id);
}

/** Compare AG Grid cell old/new values (handles select objects by id). */
export function isGridCellValueEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a && b && typeof a === "object" && typeof b === "object") {
    const aId = (a as { id?: unknown }).id;
    const bId = (b as { id?: unknown }).id;
    if (aId != null && bId != null) return aId === bId;
  }
  return false;
}

/**
 * Order-insensitive structural key: object keys are sorted, arrays are sorted by
 * their serialized items (row arrays such as tenants are compared as sets), and
 * Dates are reduced to their timestamp.
 */
function normalizeForCompare(val: unknown): unknown {
  if (val instanceof Date) return { __date: val.getTime() };
  if (Array.isArray(val)) {
    return val
      .map(normalizeForCompare)
      .sort((x, y) => JSON.stringify(x).localeCompare(JSON.stringify(y)));
  }
  if (val && typeof val === "object") {
    const obj: Record<string, unknown> = {};
    for (const k of Object.keys(val).sort()) {
      obj[k] = normalizeForCompare((val as Record<string, unknown>)[k]);
    }
    return obj;
  }
  return val;
}

function structurallyEqual(a: unknown, b: unknown): boolean {
  return (
    JSON.stringify(normalizeForCompare(a)) ===
    JSON.stringify(normalizeForCompare(b))
  );
}

function isEmptyContainer(val: unknown): boolean {
  if (Array.isArray(val)) return val.length === 0;
  if (val instanceof Date) return false;
  return !!val && typeof val === "object" && Object.keys(val).length === 0;
}

/**
 * Compare one field of a row against its original value.
 * - Arrays: order-insensitive deep comparison.
 * - Objects that both carry a non-null `id` (select options, related records):
 *   compared by id only, so a re-fetched copy of the same option is not a change.
 * - Other objects (objects without ids): deep comparison, so edits to nested
 *   objects without an id are detected.
 * - null vs object: a change, except null vs an empty array / object.
 * - Dates: compared by timestamp.
 * - Everything else: strict equality.
 */
export function isRecordFieldEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;

  if (Array.isArray(a) && Array.isArray(b)) return structurallyEqual(a, b);

  if (typeof a === "object" && typeof b === "object") {
    if (a === null || b === null) {
      // Legacy leniency: null and an empty array / object are not a change.
      return isEmptyContainer(a ?? b);
    }
    if (a instanceof Date && b instanceof Date) {
      return Object.is(a.getTime(), b.getTime());
    }
    const aId = (a as { id?: unknown }).id;
    const bId = (b as { id?: unknown }).id;
    if (aId != null && bId != null) return aId === bId;
    return structurallyEqual(a, b);
  }

  return false;
}

function isRecordEqualToOriginal(item: object, orig: object): boolean {
  const keys = new Set([...Object.keys(item), ...Object.keys(orig)]);
  for (const k of keys) {
    if (
      !isRecordFieldEqual(
        (item as Record<string, unknown>)[k],
        (orig as Record<string, unknown>)[k],
      )
    ) {
      return false;
    }
  }
  return true;
}

export function areRecordsEqual<T extends { id?: number | string | null }>(
  current: T[],
  original: T[],
): boolean {
  if (current.length !== original.length) return false;

  const originalMap = new Map(
    original.map((item) => [rowIdKey(item.id) ?? "", item]),
  );

  return current.every((item) => {
    const rowKey = rowIdKey(item.id) ?? "";
    const orig = originalMap.get(rowKey);
    if (!orig) return false;
    return isRecordEqualToOriginal(item, orig);
  });
}

export function getChangedRecords<T extends { id?: number | string | null }>(
  current: T[],
  original: T[],
): T[] {
  const originalMap = new Map(
    original.map((item) => [rowIdKey(item.id) ?? "", item]),
  );

  return current.filter((item) => {
    const key = rowIdKey(item.id) ?? "";
    const orig = originalMap.get(key);
    if (!orig) return true;
    return !isRecordEqualToOriginal(item, orig);
  });
}

/**
 * Original rows (by id) that are no longer present in `current` — e.g. user
 * removed a row that previously existed in the database.
 */
export function getRemovedRecords<T extends { id?: number | string | null }>(
  current: T[],
  original: T[],
): T[] {
  if (!original || original.length === 0) return [];
  const currentIdSet = new Set(
    current
      .map((i) => rowIdKey(i.id))
      .filter((k) => k !== null && k !== "") as string[],
  );
  return original.filter((o) => {
    const k = rowIdKey(o.id);
    if (k === null || k === "") return false;
    return !currentIdSet.has(k);
  });
}
